/**
 * Storage utility functions for handling localStorage operations
 * and Supabase synchronization
 */
import {
  buildApiUrl,
  createAuthFetchOptions,
  handleApiResponse,
} from "../../../utils/apiUtils";
import {
  getAccessToken,
  refreshSessionIfNeeded,
} from "../../../utils/authUtils";

/**
 * Saves transaction data to localStorage
 */
export const saveTableData = (data, filters) => {
  localStorage.setItem("transactionData", JSON.stringify(data));
  localStorage.setItem("tableFilters", JSON.stringify(filters));
};

/**
 * Loads transaction data from localStorage
 */
export const loadTableData = () => {
  const savedData = localStorage.getItem("transactionData");
  return savedData ? JSON.parse(savedData) : [];
};

/**
 * Loads saved filters from localStorage
 */
export const loadFilters = () => {
  const savedFilters = localStorage.getItem("tableFilters");
  if (!savedFilters) return null;

  const parsedFilters = JSON.parse(savedFilters);

  // Convert date strings back to Date objects
  if (parsedFilters.minDate) {
    parsedFilters.minDate = new Date(parsedFilters.minDate);
  }
  if (parsedFilters.maxDate) {
    parsedFilters.maxDate = new Date(parsedFilters.maxDate);
  }
  if (parsedFilters.filterStartDate) {
    parsedFilters.filterStartDate = new Date(parsedFilters.filterStartDate);
  }
  if (parsedFilters.filterEndDate) {
    parsedFilters.filterEndDate = new Date(parsedFilters.filterEndDate);
  }

  return parsedFilters;
};

/**
 * Save all data to Supabase database
 */
export const saveAllDataToSupabase = async (
  transactions,
  rules,
  history,
  filters,
  userSettings,
  reportSettings,
) => {
  try {
    // Ensure token is fresh
    await refreshSessionIfNeeded();

    // Get access token
    const token = await getAccessToken();

    if (!token) {
      throw new Error("You must be logged in to save data");
    }

    const transactionsToSave = transactions.map(
      ({ tagInput, ...rest }) => rest,
    );

    // Prepare the request using our API utilities
    const response = await fetch(
      buildApiUrl("sync/all"),
      createAuthFetchOptions(
        {
          method: "POST",
          body: JSON.stringify({
            transactions: transactionsToSave,
            rules,
            filters,
            history,
            userSettings,
            reportSettings,
          }),
        },
        token,
      ),
    );

    // Log debug info if response fails
    if (!response.ok) {
      console.error("API response error:", {
        status: response.status,
        statusText: response.statusText,
        url: response.url,
      });
    }

    const result = await handleApiResponse(response);

    // Log detailed response for debugging
    if (!result.success) {
      console.error("Backend sync errors:", result.errors);
      console.error("Full backend response:", result);
    }

    return result;
  } catch (error) {
    console.error("Error saving data to Supabase:", error);
    console.error("Error details:", {
      name: error.name,
      message: error.message,
      stack: error.stack,
    });
    throw error;
  }
};

/**
 * Load all data from Supabase database
 */
export const loadAllDataFromSupabase = async () => {
  try {
    console.log("loadAllDataFromSupabase: Starting...");

    // Ensure token is fresh
    await refreshSessionIfNeeded();
    console.log("loadAllDataFromSupabase: Session refreshed");

    // Get access token
    const token = await getAccessToken();
    console.log(
      "loadAllDataFromSupabase: Token retrieved:",
      token ? "YES" : "NO",
    );

    if (!token) {
      throw new Error("You must be logged in to load data");
    }

    // Build the URL and log it
    const apiUrl = buildApiUrl("sync/get-all");
    console.log("loadAllDataFromSupabase: API URL:", apiUrl);

    // Create fetch options and log them
    const fetchOptions = createAuthFetchOptions(
      {
        method: "POST",
      },
      token,
    );
    console.log("loadAllDataFromSupabase: Fetch options:", {
      method: fetchOptions.method,
      headers: fetchOptions.headers,
      credentials: fetchOptions.credentials,
    });

    console.log("loadAllDataFromSupabase: About to fetch...");
    console.log("Final URL:", apiUrl);
    console.log("Final options:", JSON.stringify(fetchOptions, null, 2));

    // Prepare the request using our API utilities
    const response = await fetch(apiUrl, fetchOptions);

    console.log("loadAllDataFromSupabase: Fetch completed, response:", {
      ok: response.ok,
      status: response.status,
      statusText: response.statusText,
      url: response.url,
      type: response.type,
    });

    // Log debug info if response fails
    if (!response.ok) {
      console.error("API response error:", {
        status: response.status,
        statusText: response.statusText,
        url: response.url,
      });
    }

    return await handleApiResponse(response);
  } catch (error) {
    console.error("Error loading data from Supabase:", {
      name: error.name,
      message: error.message,
      stack: error.stack,
      cause: error.cause,
    });
    throw error;
  }
};
